/**
 * SubscriptionManagementModal - Modal de gestion des subscriptions PLUS
 * 
 * Permet de gérer la subscription Stripe: cancel, reactivate, voir détails.
 * Modal dédié aux utilisateurs PLUS pour contrôler leur abonnement mensuel.
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import StripeService from '../../services/api/stripeService';
import { formatLongDate } from '../../utils/formatDate';

interface SubscriptionManagementModalProps {
    id: string;
    companyId: string;
    accessToken: string;
    subscriptionDetails?: any; // Peut être fourni depuis parent pour éviter un GET
    onClose?: () => void;
    onSuccess?: () => void; // Callback après modification subscription
}

const SubscriptionManagementModal: React.FC<SubscriptionManagementModalProps> = ({
    id,
    companyId,
    accessToken,
    subscriptionDetails: initialSubscriptionDetails,
    onClose,
    onSuccess
}) => {
    const { t } = useTranslation();
    const closeRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const [showConfirmCancel, setShowConfirmCancel] = useState(false);
    const [immediateCancel, setImmediateCancel] = useState(false);
    
    const [loading, setLoading] = useState(!initialSubscriptionDetails);
    const [subscription, setSubscription] = useState<any>(initialSubscriptionDetails || null);
    const [processing, setProcessing] = useState(false);

    // Charger les détails subscription si non fournis
    useEffect(() => {
        if (!initialSubscriptionDetails) {
            const loadSubscription = async () => {
                setLoading(true);
                try {
                    const response = await StripeService.getSubscriptionDetails(companyId, accessToken);
                    if (response.success && response.data) {
                        setSubscription(response.data);
                    } else {
                        console.error('[SubscriptionManagementModal] Failed to load subscription:', response);
                    }
                } catch (error) {
                    console.error('[SubscriptionManagementModal] Error loading subscription:', error);
                } finally {
                    setLoading(false);
                }
            };

            loadSubscription();
        }
    }, [companyId, accessToken, initialSubscriptionDetails]);

    const handleCancelSubscription = async () => {
        setProcessing(true);
        try {
            const response = await StripeService.updateSubscription(
                companyId,
                'cancel',
                { cancel_at_period_end: !immediateCancel },
                accessToken
            );

            if (response.success) {
                alert(
                    immediateCancel
                        ? 'Subscription annulée immédiatement. Vous perdez l\'accès aux fonctionnalités PLUS.'
                        : 'Subscription programmée pour annulation à la fin de la période. Vous garderez l\'accès jusqu\'à la date de renouvellement.'
                );
                if (onSuccess) onSuccess();
                if (closeRef.current) closeRef.current.click();
                if (onClose) onClose();
            } else {
                const errorMsg = StripeService.formatErrorMessage(response);
                alert(`Erreur lors de l'annulation: ${errorMsg}`);
            }
        } catch (error) {
            console.error('[SubscriptionManagementModal] Cancel error:', error);
            alert('Erreur lors de l\'annulation de la subscription.');
        } finally {
            setProcessing(false);
            setShowConfirmCancel(false);
        }
    };

    const handleReactivateSubscription = async () => {
        setProcessing(true);
        try {
            const response = await StripeService.updateSubscription(
                companyId,
                'reactivate',
                {},
                accessToken
            );

            if (response.success) {
                alert('Subscription réactivée avec succès ! Votre accès PLUS continue normalement.');
                if (onSuccess) onSuccess();
                if (closeRef.current) closeRef.current.click();
                if (onClose) onClose();
            } else {
                const errorMsg = StripeService.formatErrorMessage(response);
                alert(`Erreur lors de la réactivation: ${errorMsg}`);
            }
        } catch (error) {
            console.error('[SubscriptionManagementModal] Reactivate error:', error);
            alert('Erreur lors de la réactivation de la subscription.');
        } finally {
            setProcessing(false);
        }
    };

    const handleOpenPortal = async () => {
        setProcessing(true);
        try {
            const returnUrl = `${window.location.origin}/dashboard/home`;
            const response = await StripeService.createPortalSession(companyId, returnUrl, accessToken);
            
            console.log('[SubscriptionManagementModal] Portal response:', response);
            
            // ✅ CORRECTION : Le backend retourne session_url, pas portal_url
            if (response.success && response.data?.session_url) {
                console.log('[SubscriptionManagementModal] Redirecting to:', response.data.session_url);
                window.location.href = response.data.session_url;
            } else {
                console.error('[SubscriptionManagementModal] Failed to open portal:', response);
                const errorMsg = StripeService.formatErrorMessage(response);
                alert(`Erreur lors de l'ouverture du portail: ${errorMsg}`);
                setProcessing(false);
            }
        } catch (error) {
            console.error('[SubscriptionManagementModal] Portal error:', error);
            alert('Erreur lors de l\'ouverture du portail Stripe.');
            setProcessing(false);
        }
    };

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }
        document.body.style.overflow = '';
    }, [onClose]);

    useEffect(() => {
        const modalElement = modalRef.current;
        if (modalElement) {
            const handleModalHidden = () => {
                handleClose();
            };
            modalElement.addEventListener('hidden.bs.modal', handleModalHidden);
            document.body.style.overflow = 'hidden';

            // Ouvrir le modal automatiquement
            const bootstrap = (window as any).bootstrap;
            if (bootstrap) {
                const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
                modal.show();
            }

            return () => {
                modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
                document.body.style.overflow = '';
            };
        }
    }, [handleClose]);

    const getStatusBadgeClass = (status: string): string => {
        switch (status) {
            case 'active': return 'bg-success';
            case 'canceled': return 'bg-danger';
            case 'past_due': return 'bg-warning text-dark';
            case 'incomplete': return 'bg-secondary';
            case 'trialing': return 'bg-info';
            default: return 'bg-secondary';
        }
    };

    const formatDate = (dateInput: number | string): string => {
        // ✅ Accepter timestamp Unix (number) OU ISO string
        let date: Date;
        
        if (typeof dateInput === 'string') {
            // Backend retourne ISO string
            date = new Date(dateInput);
        } else {
            // Ou timestamp Unix en secondes
            date = new Date(dateInput * 1000);
        }
        
        // Vérifier que la date est valide
        if (isNaN(date.getTime())) {
            console.error('[SubscriptionManagementModal] Invalid date:', dateInput);
            return 'Date invalide';
        }
        
        // Utiliser notre fonction utilitaire avec la date construite
        return formatLongDate(date);
    };

    const modalContent = (
        <div
            ref={modalRef}
            className="modal fade"
            data-bs-backdrop="true"
            data-bs-keyboard="true"
            id={id}
            tabIndex={-1}
            aria-labelledby={`${id}Label`}
            aria-hidden="true"
            style={{ zIndex: 10000, position: 'fixed' }}
        >
            <div className="modal-dialog modal-dialog-centered modal-lg" style={{ zIndex: 10001, pointerEvents: 'auto' }}>
                <div className="modal-content" style={{ zIndex: 10002, pointerEvents: 'auto', backgroundColor: '#1a1a1a', borderRadius: '15px' }}>
                    <div className="modal-header border-secondary" style={{ borderRadius: '15px 15px 0 0' }}>
                        <h5 className="modal-title text-theme" id={`${id}Label`}>
                            <i className="fa-solid fa-cog me-2"></i>
                            {t('views.modal.subscription.title', 'Manage My Subscription')}
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        ></button>
                    </div>
                    <div className="modal-body text-white p-4">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">{t('views.modal.subscription.loading', 'Loading details...')}</p>
                            </div>
                        ) : subscription ? (
                            <>
                                {/* Détails subscription */}
                                <div className="card border-secondary mb-4" style={{ borderRadius: '12px', backgroundColor: '#2d2d2d' }}>
                                    <div className="card-body p-4">
                                        <h6 className="mb-3" style={{ color: '#4fc3f7', fontSize: '1rem', fontWeight: '600' }}>
                                            <i className="fa-solid fa-info-circle me-2"></i>
                                            {t('views.modal.subscription.details', 'Subscription Details')}
                                        </h6>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <div className="d-flex align-items-center mb-3">
                                                    <i className="fa-solid fa-circle-check me-3" style={{ width: '25px', fontSize: '1.2rem', color: '#4caf50' }}></i>
                                                    <div>
                                                        <small className="d-block" style={{ color: '#9e9e9e', fontSize: '0.85rem', marginBottom: '4px' }}>
                                                            {t('views.modal.subscription.statusLabel', 'Statut')}
                                                        </small>
                                                        <span className={`badge ${getStatusBadgeClass(subscription.status)} px-3 py-2`} style={{ fontSize: '0.9rem' }}>
                                                            {subscription.status.toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="d-flex align-items-center mb-3">
                                                    <i className="fa-solid fa-dollar-sign me-3" style={{ width: '25px', fontSize: '1.2rem', color: '#ffc107' }}></i>
                                                    <div>
                                                        <small className="d-block" style={{ color: '#9e9e9e', fontSize: '0.85rem', marginBottom: '4px' }}>
                                                            {t('views.modal.subscription.monthlyAmount', 'Montant mensuel')}
                                                        </small>
                                                        <strong style={{ color: '#ffffff', fontSize: '1.1rem' }}>${(subscription.amount / 100).toFixed(2)} USD</strong>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="d-flex align-items-center">
                                                    <i className="fa-solid fa-calendar-alt me-3" style={{ width: '25px', fontSize: '1.2rem', color: '#29b6f6' }}></i>
                                                    <div>
                                                        <small className="d-block" style={{ color: '#9e9e9e', fontSize: '0.85rem', marginBottom: '4px' }}>
                                                            {t('views.modal.subscription.nextBillingDate', 'Next Billing')}
                                                        </small>
                                                        <strong style={{ color: '#ffffff', fontSize: '1rem' }}>{formatDate(subscription.current_period_end)}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                            {subscription.cancel_at_period_end && (
                                                <div className="col-md-6">
                                                    <div className="d-flex align-items-center">
                                                        <i className="fa-solid fa-exclamation-triangle me-3" style={{ width: '25px', fontSize: '1.2rem', color: '#ff9800' }}></i>
                                                        <div>
                                                            <small className="d-block" style={{ color: '#9e9e9e', fontSize: '0.85rem', marginBottom: '4px' }}>
                                                                {t('views.modal.subscription.cancelScheduledLabel', 'Scheduled Cancellation')}
                                                            </small>
                                                            <strong className="text-warning" style={{ fontSize: '1rem' }}>
                                                                {t('views.modal.subscription.endOfPeriod', 'End of Period')}
                                                            </strong>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Annulation programmée ? */}
                                {subscription.cancel_at_period_end && (
                                    <div className="alert alert-warning mb-4">
                                        <h6 className="mb-2">
                                            <i className="fa-solid fa-info-circle me-2"></i>
                                            {t('views.modal.subscription.cancelScheduled', 'Scheduled Cancellation')}
                                        </h6>
                                        <p className="mb-3 small">
                                            {t('views.modal.subscription.cancelScheduledDescription', 
                                                'Votre subscription sera annulée le')} <strong>{formatDate(subscription.current_period_end)}</strong>.
                                            {t('views.modal.subscription.keepAccess', 'You will keep access to PLUS features until this date.')}
                                        </p>
                                        <button
                                            className="btn btn-success btn-sm"
                                            onClick={handleReactivateSubscription}
                                            disabled={processing}
                                        >
                                            {processing ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {t('views.modal.subscription.processing', 'Traitement...')}
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa-solid fa-undo me-2"></i>
                                                    {t('views.modal.subscription.reactivate', 'Reactivate')}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}

                                {/* Actions */}
                                {!showConfirmCancel ? (
                                    <div className="d-grid gap-3">
                                        {!subscription.cancel_at_period_end && (
                                            <button
                                                className="btn"
                                                onClick={() => setShowConfirmCancel(true)}
                                                disabled={processing}
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    borderColor: '#f44336',
                                                    color: '#f44336',
                                                    borderWidth: '2px',
                                                    fontWeight: '600',
                                                    padding: '12px',
                                                    fontSize: '1rem'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#f44336';
                                                    e.currentTarget.style.color = '#ffffff';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                    e.currentTarget.style.color = '#f44336';
                                                }}
                                            >
                                                <i className="fa-solid fa-times-circle me-2"></i>
                                                {t('views.modal.subscription.cancelButton', 'Annuler la subscription')}
                                            </button>
                                        )}
                                        <button
                                            className="btn"
                                            onClick={handleOpenPortal}
                                            disabled={processing}
                                            style={{
                                                backgroundColor: 'transparent',
                                                borderColor: '#2196f3',
                                                color: '#2196f3',
                                                borderWidth: '2px',
                                                fontWeight: '600',
                                                padding: '12px',
                                                fontSize: '1rem'
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.backgroundColor = '#2196f3';
                                                e.currentTarget.style.color = '#ffffff';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                e.currentTarget.style.color = '#2196f3';
                                            }}
                                        >
                                            {processing ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {t('views.modal.subscription.opening', 'Ouverture...')}
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa-brands fa-stripe me-2"></i>
                                                    {t('views.modal.subscription.openPortal', 'Manage via Stripe Portal')}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="card bg-danger bg-opacity-10 border-danger" style={{ borderRadius: '12px' }}>
                                        <div className="card-body p-4">
                                            <h6 className="text-danger mb-3">
                                                <i className="fa-solid fa-exclamation-triangle me-2"></i>
                                                {t('views.modal.subscription.confirmCancel', 'Confirmer l\'annulation')}
                                            </h6>
                                            <p className="text-white mb-3">
                                                {t('views.modal.subscription.confirmCancelDescription', 
                                                    'Êtes-vous sûr de vouloir annuler votre subscription PLUS ?')}
                                            </p>
                                            <div className="form-check mb-3">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="immediateCancelCheck"
                                                    checked={immediateCancel}
                                                    onChange={(e) => setImmediateCancel(e.target.checked)}
                                                />
                                                <label className="form-check-label text-white small" htmlFor="immediateCancelCheck">
                                                    {t('views.modal.subscription.immediateCancel', 
                                                        'Annuler immédiatement (vous perdez l\'accès maintenant)')}
                                                </label>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-secondary flex-grow-1"
                                                    onClick={() => {
                                                        setShowConfirmCancel(false);
                                                        setImmediateCancel(false);
                                                    }}
                                                    disabled={processing}
                                                >
                                                    <i className="fa-solid fa-arrow-left me-2"></i>
                                                    {t('words.back', 'Retour')}
                                                </button>
                                                <button
                                                    className="btn btn-danger flex-grow-1"
                                                    onClick={handleCancelSubscription}
                                                    disabled={processing}
                                                >
                                                    {processing ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            {t('views.modal.subscription.processing', 'Traitement...')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa-solid fa-check me-2"></i>
                                                            {immediateCancel 
                                                                ? t('views.modal.subscription.confirmImmediateCancel', 'Annuler maintenant')
                                                                : t('views.modal.subscription.confirmEndPeriodCancel', 'Annuler à la fin de période')}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="alert alert-danger">
                                {t('views.modal.subscription.error', 'Impossible de charger les détails de la subscription')}
                            </div>
                        )}
                    </div>
                    <div className="modal-footer border-secondary">
                        <button
                            ref={closeRef}
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                            disabled={processing}
                        >
                            <i className="fa-solid fa-times me-2"></i>
                            {t('words.close', 'Fermer')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default SubscriptionManagementModal;
